import { fileStorage } from '../utils/FileStorage.js';
import { featureFlags } from '../services/FeatureFlagService.js';
import { SUBSCRIPTION_TIERS, formatTierPricing } from '../config/subscriptionTiers.js';
import { isTextEditable } from '../utils/helpers.js';

export class UIManager {
    constructor(accountManager, stateService) {
        this.accountManager = accountManager;
        this.stateService = stateService;

        // --- DOM Elements ---
        this.authBtn = document.getElementById('auth-btn');
        this.logoutBtn = document.getElementById('logout-btn');
        this.loginModal = document.getElementById('login-modal');
        this.closeModal = document.getElementById('close-modal');
        this.loginForm = document.getElementById('login-form');
        this.userInfo = document.getElementById('user-info');
        this.userName = document.getElementById('user-name');
        this.userAvatar = document.getElementById('user-avatar');
        this.userTrigger = document.getElementById('user-trigger');
        this.userDropdown = document.getElementById('user-dropdown');
        this.userEmail = document.getElementById('user-email');
        this.licenseCount = document.getElementById('license-count');
        this.signinView = document.getElementById('signin-view');
        this.signupView = document.getElementById('signup-view');
        this.showSignupLink = document.getElementById('show-signup');
        this.showSigninLink = document.getElementById('show-signin');
        this.signupForm = document.getElementById('signup-form');
        this.verifyBanner = document.getElementById('verify-banner');
        this.mainEl = document.querySelector('#app-view main');
        this.speedSlider = document.getElementById('speed-slider');
        this.speedLabel = document.getElementById('speed-label');
        this.visualizerSensitivitySlider = document.getElementById('visualizer-threshold');

        // Custom Dropdown Elements
        this.fileDropdownContainer = document.getElementById('file-dropdown-container');
        this.fileDropdownTrigger = document.getElementById('file-dropdown-trigger');
        this.fileDropdownOptions = document.getElementById('file-dropdown-options');
        this.currentFileLabel = document.getElementById('current-file-label');

        this.charCount = document.getElementById('char-count');
        this.feedbackBtn = document.getElementById('feedback-btn');
        this.feedbackModal = document.getElementById('feedback-modal');
        this.feedbackForm = document.getElementById('feedback-form');
        this.feedbackBody = document.getElementById('feedback-body');
        this.closeFeedbackBtn = document.getElementById('close-feedback');
        this.closeFeedbackX = document.getElementById('close-feedback-x');

        // Sidebar membership widgets.
        this.sidebarMembership = document.getElementById('sidebar-membership');
        this.sidebarUserTier = document.getElementById('sidebar-user-tier');
        this.sidebarUpgradeBtn = document.getElementById('sidebar-upgrade-btn');
        this.upgradeModal = document.getElementById('upgrade-modal');
        this.isUpgradeYearlyBilling = true;
        this.selectedUpgradeTier = null;
        this.upgradePricingData = null;

        window.addEventListener('purchase-success', () => this.showPurchaseSuccess());
        this.initListeners();
        this.updateMembershipUI(this.accountManager?.currentUser);
        this.updateMidiUI();
        this.updateShortcutsUI();
    }

    initListeners() {
        this.authBtn?.addEventListener('click', () => this.loginModal?.classList.remove('hidden'));
        this.closeModal?.addEventListener('click', () => this.closeModalFn());
        document.getElementById('close-modal-x')?.addEventListener('click', () => this.closeModalFn());
        document.querySelectorAll('.cancel-auth').forEach(btn => btn.addEventListener('click', () => this.closeModalFn()));

        this.showSignupLink?.addEventListener('click', (e) => {
            e.preventDefault();
            this.signinView.classList.add('hidden');
            this.signupView.classList.remove('hidden');
            document.getElementById('forgot-password-view').classList.add('hidden');
        });

        this.showSigninLink?.addEventListener('click', (e) => {
            e.preventDefault();
            this.signupView.classList.add('hidden');
            this.signinView.classList.remove('hidden');
            document.getElementById('forgot-password-view').classList.add('hidden');
        });

        document.getElementById('show-forgot-password')?.addEventListener('click', (e) => {
            e.preventDefault();
            this.signinView.classList.add('hidden');
            document.getElementById('forgot-password-view').classList.remove('hidden');
        });

        document.getElementById('back-to-signin')?.addEventListener('click', (e) => {
            e.preventDefault();
            document.getElementById('forgot-password-view').classList.add('hidden');
            this.signinView.classList.remove('hidden');
        });

        this.userTrigger?.addEventListener('click', (e) => {
            e.stopPropagation();
            this.userDropdown.classList.toggle('hidden');
        });

        document.addEventListener('click', (e) => {
            if (this.userDropdown && !this.userDropdown.classList.contains('hidden')) {
                this.userDropdown.classList.add('hidden');
            }
            // Close file dropdown if clicking outside
            if (this.fileDropdownOptions && !this.fileDropdownOptions.classList.contains('hidden')) {
                if (!this.fileDropdownContainer.contains(e.target)) {
                    this.fileDropdownOptions.classList.add('hidden');
                }
            }
        });

        // Global Key Listeners
        document.addEventListener('keydown', (e) => {
            if (e.code === 'Space') {
                // Ignore if typing in an input or textarea
                if (isTextEditable(document.activeElement)) {
                    return;
                }

                e.preventDefault(); // Prevent scrolling
                const playBtn = document.getElementById('play-btn');
                if (playBtn) {
                    playBtn.click();
                }
            }
        });

        this.logoutBtn?.addEventListener('click', () => this.accountManager.logout());

        // --- Auth Form Submissions ---
        this.loginForm?.addEventListener('submit', (e) => this.handleLoginSubmit(e));
        this.signupForm?.addEventListener('submit', (e) => this.handleSignupSubmit(e));
        document.getElementById('forgot-password-form')?.addEventListener('submit', (e) => this.handleForgotSubmit(e));
        document.getElementById('reset-password-form')?.addEventListener('submit', (e) => this.handleResetSubmit(e));

        // --- Social Login ---
        document.getElementById('google-login')?.addEventListener('click', () => this.handleSocialLogin('google'));

        // --- Verification ---
        document.getElementById('resend-verify')?.addEventListener('click', () => this.handleResendVerification());

        // Check for Reset Token or Purchase Success
        this.checkUrlForResetToken();
        this.checkUrlForPurchaseSuccess();

        // --- EQ Toggle ---
        document.getElementById('eq-toggle')?.addEventListener('click', () => {
            const container = document.querySelector('.eq-container');
            container.classList.toggle('collapsed');
            const isCollapsed = container.classList.contains('collapsed');
            document.getElementById('eq-toggle').textContent = isCollapsed ? '▼' : '▲';
            this.stateService.saveState({ eqCollapsed: isCollapsed }, this.accountManager.token);
        });
        // --- Visualizer Toggle ---
        document.getElementById('visualizer-toggle')?.addEventListener('click', () => {
            const container = document.querySelector('.visualizer-container');
            container.classList.toggle('collapsed');
            const isCollapsed = container.classList.contains('collapsed');
            document.getElementById('visualizer-toggle').textContent = isCollapsed ? '▼' : '▲';
            this.stateService.saveState({ visualizerCollapsed: isCollapsed }, this.accountManager.token);
        });

        // --- Shortcuts Toggle ---
        document.getElementById('shortcuts-toggle')?.addEventListener('click', () => {
            const container = document.getElementById('shortcuts-container');
            container.classList.toggle('collapsed');
            const isCollapsed = container.classList.contains('collapsed');
            document.getElementById('shortcuts-toggle').textContent = isCollapsed ? '▼' : '▲';
            this.stateService.saveState({ shortcutsCollapsed: isCollapsed }, this.accountManager.token);
        });

        this.visualizerSensitivitySlider?.addEventListener('input', (e) => {
            const val = parseFloat(e.target.value);
            this.stateService.saveState({ visualizerThreshold: val }, this.accountManager.token);
        });

        if (this.visualizerSensitivitySlider) {
            this.visualizerSensitivitySlider.value = (this.stateService.get('visualizerThreshold') || 0.0).toString();
        }

        // --- Visualizer Mode Toggles ---
        const visModeButtons = document.querySelectorAll('.visualizer-mode-group button');
        visModeButtons.forEach(btn => {
            btn.addEventListener('click', () => {
                const mode = btn.dataset.mode;
                this.updateVisualizerModeUI(mode);
                this.stateService.saveState({ visualizerMode: mode }, this.accountManager.token);
            });
        });
        const initialVisMode = this.stateService.get('visualizerMode') || 'hybrid';
        this.updateVisualizerModeUI(initialVisMode);


        // --- Settings Modal ---
        this.settingsModal = document.getElementById('settings-modal');
        this.settingsForm = document.getElementById('settings-form');
        this.closeSettingsBtn = document.getElementById('close-settings');
        this.closeSettingsX = document.getElementById('close-settings-x');
        this.clearCacheBtn = document.getElementById('clear-cache-btn');
        this.themeSelect = document.getElementById('theme-select');

        document.getElementById('docs-btn')?.addEventListener('click', () => {
            window.open('documentation.html', '_blank');
        });

        document.getElementById('user-settings-btn')?.addEventListener('click', () => {
            if (this.settingsModal) {
                // Load current theme
                const currentTheme = this.stateService.get('theme') || 'dark';
                if (this.themeSelect) this.themeSelect.value = currentTheme;

                this.settingsModal.classList.remove('hidden');
                this.userDropdown.classList.add('hidden'); // Close dropdown
            }
        });

        // --- User Settings ---
        const closeSettingsFn = () => this.settingsModal.classList.add('hidden');
        this.closeSettingsBtn?.addEventListener('click', closeSettingsFn);
        this.closeSettingsX?.addEventListener('click', closeSettingsFn);

        this.clearCacheBtn?.addEventListener('click', () => this.handleClearCache());

        // Instant Theme Switching
        const themeSelect = document.getElementById('theme-select');
        themeSelect?.addEventListener('change', (e) => {
            const newTheme = e.target.value;
            this.applyTheme(newTheme);
            this.stateService.saveState({ theme: newTheme }, this.accountManager.token);
        });

        // Prevent form submission behavior since we handle it on change
        this.settingsForm?.addEventListener('submit', (e) => {
            e.preventDefault();
            closeSettingsFn();
        });

        // Initial Theme Application
        const initialTheme = this.stateService.get('theme') || 'dark';
        this.applyTheme(initialTheme);


        // --- Feedback ---
        this.feedbackBtn?.addEventListener('click', () => {
            this.feedbackModal?.classList.remove('hidden');
        });

        this.closeFeedbackBtn?.addEventListener('click', () => this.closeFeedbackModal());
        this.closeFeedbackX?.addEventListener('click', () => this.closeFeedbackModal());

        this.feedbackBody?.addEventListener('input', () => {
            if (this.charCount) {
                this.charCount.textContent = `${this.feedbackBody.value.length} / 512`;
            }
        });

        this.feedbackForm?.addEventListener('submit', (e) => this.handleFeedbackSubmit(e));

        // --- Dropdown Actions ---
        document.getElementById('admin-btn')?.addEventListener('click', () => {
            this.mainEl?.classList.add('hidden');
            document.querySelector('.sidebar')?.classList.add('hidden'); // Hide sidebar
            document.getElementById('admin-view').classList.remove('hidden');
            this.userDropdown.classList.add('hidden');

            // Trigger admin settings load if callback handles it, or rely on tab clicks
            // Ideally we should load settings here if not loaded
        });

        // Add Back to App button listener
        document.getElementById('admin-back-btn')?.addEventListener('click', () => {
            document.getElementById('admin-view').classList.add('hidden');
            this.mainEl?.classList.remove('hidden');
            document.querySelector('.sidebar')?.classList.remove('hidden'); // Show sidebar
        });

        document.getElementById('profile-btn')?.addEventListener('click', () => {
            this.renderProfileModal();
            this.userDropdown.classList.add('hidden');
        });

        // --- Profile Modal Listeners ---
        const closeProfileFn = () => document.getElementById('profile-modal').classList.add('hidden');
        document.getElementById('close-profile-x')?.addEventListener('click', closeProfileFn);
        document.getElementById('close-profile-btn')?.addEventListener('click', closeProfileFn);

        document.getElementById('change-password-form')?.addEventListener('submit', (e) => this.handleChangePasswordSubmit(e));

        document.getElementById('init-delete-account-btn')?.addEventListener('click', () => {
            document.getElementById('init-delete-account-btn').classList.add('hidden');
            document.getElementById('delete-account-confirm').classList.remove('hidden');
        });

        document.getElementById('cancel-delete-account')?.addEventListener('click', () => {
            document.getElementById('delete-account-confirm').classList.add('hidden');
            document.getElementById('init-delete-account-btn').classList.remove('hidden');
        });

        document.getElementById('confirm-delete-account')?.addEventListener('click', () => this.handleDeleteAccountSubmit());

        // --- Purchase Modal ---
        document.getElementById('add-credits-btn')?.addEventListener('click', () => this.showPurchaseModal());
        document.getElementById('get-licenses-btn')?.addEventListener('click', () => this.showPurchaseModal());
        document.getElementById('close-purchase-x')?.addEventListener('click', () => document.getElementById('purchase-modal').classList.add('hidden'));

        // --- Upgrade Membership Modal ---
        this.sidebarUpgradeBtn?.addEventListener('click', () => this.showUpgradeModal());
        document.getElementById('close-upgrade-x')?.addEventListener('click', () => this.hideUpgradeModal());
        document.getElementById('close-upgrade-btn')?.addEventListener('click', async () => {
            const btn = document.getElementById('close-upgrade-btn');
            if (btn?.dataset?.action === 'purchase') {
                await this.handleUpgradePurchase(btn);
            } else {
                this.hideUpgradeModal();
            }
        });
        document.getElementById('upgrade-billing-toggle')?.addEventListener('click', () => {
            this.isUpgradeYearlyBilling = !this.isUpgradeYearlyBilling;
            document.getElementById('upgrade-opt-monthly')?.classList.toggle('active', !this.isUpgradeYearlyBilling);
            document.getElementById('upgrade-opt-yearly')?.classList.toggle('active', this.isUpgradeYearlyBilling);
            this.renderUpgradeTiers();
            this.updateUpgradeActionButton();
        });

        // --- Warning Modal ---
        this.warningModal = document.getElementById('warning-modal');
        this.warningCancelBtn = document.getElementById('warning-cancel-btn');
        this.warningProceedBtn = document.getElementById('warning-proceed-btn');
    }

    checkUrlForResetToken() {
        const urlParams = new URLSearchParams(window.location.search);
        const resetToken = urlParams.get('resetToken');

        if (resetToken) {
            this.loginModal.classList.remove('hidden');
            this.signinView.classList.add('hidden');
            this.signupView.classList.add('hidden');
            document.getElementById('forgot-password-view').classList.add('hidden');

            const resetView = document.getElementById('reset-password-view');
            resetView.classList.remove('hidden');
            document.getElementById('reset-token').value = resetToken;

            // Clean URL
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }

    /**
     * Checks if redirecting back from a successful purchase and refreshes user profile.
     */
    async checkUrlForPurchaseSuccess() {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('purchase') === 'success') {
            try {
                if (this.accountManager) {
                    const user = await this.accountManager.refreshProfile();
                    this.updateAuthUI(user);
                    this.updateMembershipUI(user);
                }
            } catch (err) {
                console.warn('[UIManager] Failed to refresh profile after purchase:', err);
            }
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }

    applyTheme(theme) {
        if (theme === 'light') {
            document.body.classList.add('light-mode');
        } else {
            document.body.classList.remove('light-mode');
        }
    }

    async handleLoginSubmit(e) {
        e.preventDefault();
        const email = document.getElementById('email').value;
        const pass = document.getElementById('password').value;
        const submitBtn = this.loginForm.querySelector('button[type="submit"]');

        try {
            submitBtn.textContent = 'Signing In...';
            submitBtn.disabled = true;
            await this.accountManager.login(email, pass);
            this.closeModalFn();
        } catch (err) {
            alert(err.message);
            submitBtn.textContent = 'Sign In';
            submitBtn.disabled = false;
        }
    }

    async handleSignupSubmit(e) {
        e.preventDefault();
        const name = document.getElementById('signup-name').value;
        const email = document.getElementById('signup-email').value;
        const pass = document.getElementById('signup-password').value;
        const submitBtn = this.signupForm.querySelector('button[type="submit"]');

        try {
            submitBtn.textContent = 'Creating Account...';
            submitBtn.disabled = true;
            const data = await this.accountManager.signUp(email, pass, name);

            if (data.waitlist) {
                // User is waitlisted
                if (data.waitlist.pendingVerification) {
                    // Show "Check Inbox" UI
                    this.signupForm.innerHTML = `
                        <div style="text-align: center; padding: 20px;">
                            <div style="font-size: 3rem; margin-bottom: 1rem;">📩</div>
                            <h3 style="font-size: 1.5rem; margin-bottom: 1rem; color: var(--text-primary);">Check your inbox</h3>
                            <p style="margin-bottom: 1rem; color: var(--text-secondary);">You're one step away!</p>
                            <p style="margin-bottom: 1.5rem; color: var(--text-secondary);">
                                We've sent a confirmation link to <strong style="color: var(--text-primary);">${email}</strong>.<br>
                                Click it to secure your spot on the list.
                            </p>
                            <button class="auth-btn" onclick="location.reload()">Got it</button>
                        </div>
                    `;
                    // Do not close modal automatically, let user read and click "Got it"
                } else {
                    alert(`You're on the list! You are #${data.waitlist.position} in line for the next wave of invites. Check your inbox for updates.`);
                    this.closeModalFn();
                }

                // Ensure no partial session exists
                this.accountManager.silentLogout();
            } else {
                // Success - Auto login handled by onChanged/redirect in landing.js
                this.closeModalFn();
            }
        } catch (err) {
            alert(err.message);
            submitBtn.textContent = 'Create Account';
            submitBtn.disabled = false;
        }
    }

    async handleForgotSubmit(e) {
        e.preventDefault();
        const email = document.getElementById('forgot-email').value;
        const submitBtn = e.target.querySelector('button[type="submit"]');

        try {
            submitBtn.textContent = 'Sending...';
            submitBtn.disabled = true;
            const res = await this.accountManager.requestPasswordReset(email);
            alert(res.message);
            this.closeModalFn();
        } catch (err) {
            alert(err.message);
        } finally {
            submitBtn.textContent = 'Send Reset Link';
            submitBtn.disabled = false;
        }
    }

    async handleResetSubmit(e) {
        e.preventDefault();
        const token = document.getElementById('reset-token').value;
        const pass = document.getElementById('new-password').value;
        const confirmPass = document.getElementById('confirm-new-password').value;
        const submitBtn = e.target.querySelector('button[type="submit"]');

        if (pass !== confirmPass) {
            alert("Passwords do not match");
            return;
        }

        try {
            submitBtn.textContent = 'Updating...';
            submitBtn.disabled = true;
            const res = await this.accountManager.resetPassword(token, pass);
            alert(res.message);
            this.closeModalFn();
        } catch (err) {
            alert(err.message);
        } finally {
            submitBtn.textContent = 'Update Password';
            submitBtn.disabled = false;
        }
    }

    async handleSocialLogin(provider) {
        if (provider === 'google') {
            const apiUrl = this.accountManager.API_URL.replace(/\/api$/, '');
            window.location.href = `${apiUrl}/auth/google?origin=${encodeURIComponent(window.location.origin)}`;
            return;
        }
        try {
            await this.accountManager.socialLogin(provider);
            this.closeModalFn();
        } catch (err) {
            alert(err.message);
        }
    }

    async handleResendVerification() {
        const btn = document.getElementById('resend-verify');
        if (!btn) return;
        const originalText = btn.textContent;
        try {
            btn.textContent = 'Sending...';
            btn.disabled = true;
            const result = await this.accountManager.resendVerification();
            alert(result.message || 'Verification email sent!');
        } catch (err) {
            alert('Failed to resend: ' + err.message);
        } finally {
            btn.textContent = originalText;
            btn.disabled = false;
        }
    }

    closeModalFn() {
        this.loginModal?.classList.add('hidden');
        this.signinView?.classList.remove('hidden');
        this.signupView?.classList.add('hidden');
        document.getElementById('forgot-password-view')?.classList.add('hidden');
        document.getElementById('reset-password-view')?.classList.add('hidden');

        // Reset forms
        this.loginForm?.reset();
        this.signupForm?.reset();
        document.getElementById('forgot-password-form')?.reset();
        document.getElementById('reset-password-form')?.reset();

        // Reset buttons
        const loginSubmitBtn = this.loginForm?.querySelector('button[type="submit"]');
        if (loginSubmitBtn) {
            loginSubmitBtn.textContent = 'Sign In';
            loginSubmitBtn.disabled = false;
        }

        const signupSubmitBtn = this.signupForm?.querySelector('button[type="submit"]');
        if (signupSubmitBtn) {
            signupSubmitBtn.textContent = 'Create Account';
            signupSubmitBtn.disabled = false;
        }
    }

    showSignIn() {
        if (!this.loginModal) return;
        if (featureFlags.isEnabled('free_mode')) return;
        this.loginModal.classList.remove('hidden');
        this.signinView?.classList.remove('hidden');
        this.signupView?.classList.add('hidden');
        document.getElementById('forgot-password-view')?.classList.add('hidden');
    }

    showSignUp() {
        if (!this.loginModal) return;
        if (featureFlags.isEnabled('free_mode')) return;
        this.loginModal.classList.remove('hidden');
        this.signinView?.classList.add('hidden');
        this.signupView?.classList.remove('hidden');
        document.getElementById('forgot-password-view')?.classList.add('hidden');
    }



    refreshFeedbackButtonVisibility() {
        const user = this.accountManager.currentUser;
        if (!this.feedbackBtn) return;

        if (!user) {
            this.feedbackBtn.classList.add('hidden');
            return;
        }

        const canFeedback = user.can_provide_feedback;
        const isAdmin = this.accountManager.isAdmin();

        // Relaxed condition: removed hasPlayed requirement
        if (canFeedback && !isAdmin) {
            this.feedbackBtn.classList.remove('hidden');
        } else {
            this.feedbackBtn.classList.add('hidden');
        }
    }

    updateBetaUI(user) {
        let betaBadge = document.getElementById('beta-badge');

        // Create badge if it doesn't exist
        if (!betaBadge) {
            betaBadge = document.createElement('div');
            betaBadge.id = 'beta-badge';
            betaBadge.className = 'beta-badge hidden';
            betaBadge.textContent = 'BETA';
            // Insert after brand logo in sidebar if possible, or header
            const brandContainer = document.querySelector('.sidebar .brand');
            if (brandContainer) {
                brandContainer.appendChild(betaBadge);
            }
        }

        const creditsDisplay = document.getElementById('credits-display');
        const addCreditsBtn = document.getElementById('add-credits-btn');
        const getLicensesBtn = document.getElementById('get-licenses-btn');

        if (featureFlags.isEnabled('free_mode')) {
            // Free Mode ON: hide all credit and payment UI controls
            betaBadge.classList.add('hidden');
            if (creditsDisplay) creditsDisplay.classList.add('hidden');
            if (addCreditsBtn) addCreditsBtn.classList.add('hidden');
            if (getLicensesBtn) getLicensesBtn.classList.add('hidden');
        } else if (user && user.beta_mode) {
            // Beta Mode ON
            betaBadge.classList.remove('hidden');
            if (creditsDisplay) creditsDisplay.classList.remove('hidden'); // Show credits count
            if (addCreditsBtn) addCreditsBtn.classList.add('hidden');      // Hide purchase button (+)
            if (getLicensesBtn) getLicensesBtn.classList.add('hidden');    // Hide purchase menu item
        } else {
            // Beta Mode OFF
            betaBadge.classList.add('hidden');
            if (creditsDisplay) creditsDisplay.classList.remove('hidden');
            if (addCreditsBtn) addCreditsBtn.classList.remove('hidden');
            if (getLicensesBtn) getLicensesBtn.classList.remove('hidden');
        }
    }

    /**
     * Updates landing page specific elements based on system feature flags.
     */
    updateLandingUI() {
        const startAppBtn = document.getElementById('start-app-btn');
        if (startAppBtn) {
            if (featureFlags.isEnabled('free_mode')) {
                startAppBtn.innerHTML = 'Try it now for free! <span class="arrow">→</span>';
            } else {
                startAppBtn.innerHTML = 'Request Early Access <span class="arrow">→</span>';
            }
        }
    }

    /**
     * Updates visibility of the Connect MIDI button based on feature flag and browser support.
     */
    updateMidiUI() {
        const midiBtn = document.getElementById('midi-btn');
        if (!midiBtn) return;
        if (!navigator.requestMIDIAccess || !featureFlags.isEnabled('connect_midi')) {
            midiBtn.style.display = 'none';
        } else {
            midiBtn.style.display = 'flex';
        }
    }

    /**
     * Updates visibility of keyboard shortcut items based on active feature flags.
     */
    updateShortcutsUI() {
        const shortcutItems = document.querySelectorAll('#shortcuts-grid .shortcut-item[data-flag]');
        shortcutItems.forEach(item => {
            const flag = item.dataset?.flag || item.getAttribute?.('data-flag');
            if (flag) {
                const isEnabled = featureFlags.isEnabled(flag);
                item.style.display = isEnabled ? '' : 'none';
            }
        });
    }

    /**
     * Updates the membership tier widget and upgrade button in the sidebar based on free mode.
     * @param {Object|null} [user=null] - The current user object or null.
     */
    updateMembershipUI(user = null) {
        if (!this.sidebarMembership) return;
        const isFreeMode = featureFlags.isEnabled('free_mode');
        if (isFreeMode) {
            this.sidebarMembership.classList.remove('hidden');
            if (this.sidebarUserTier) {
                const effectiveUser = user || this.accountManager?.currentUser;
                const tier = (effectiveUser && effectiveUser.tier) ? effectiveUser.tier : 'Free';
                this.sidebarUserTier.textContent = tier.charAt(0).toUpperCase() + tier.slice(1);
            }
        } else {
            this.sidebarMembership.classList.add('hidden');
        }
    }

    updateAuthUI(user, callbacks = {}) {
        const { loadAdminSettings, applyEQ, updateEQGains, restoreSession, resetAppState, loadFile } = callbacks;
        if (loadFile) this.loadFileCallback = loadFile;
        if (resetAppState) this.resetAppStateCallback = resetAppState;

        this.updateLandingUI();
        this.updateMidiUI();
        this.updateShortcutsUI();
        this.updateMembershipUI(user);

        if (user) {
            this.authBtn?.classList.add('hidden');
            this.userInfo?.classList.remove('hidden');

            // Show credits display (only when free_mode is disabled)
            const creditsDisplay = document.getElementById('credits-display');
            if (creditsDisplay) {
                if (featureFlags.isEnabled('free_mode')) {
                    creditsDisplay.classList.add('hidden');
                } else {
                    const countEl = document.getElementById('header-credits-count');
                    if (countEl) countEl.textContent = `${user.license_credits || 0} Credits`;
                }
            }

            this.updateBetaUI(user);

            if (this.userAvatar) this.userAvatar.src = user.avatar || 'https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y';

            // Set User Name in the Trigger
            if (this.userName) {
                // Show only first name
                const firstName = (user.name || 'User').split(' ')[0];
                this.userName.textContent = firstName;
                this.userName.classList.remove('hidden');
            }

            if (this.userEmail) this.userEmail.textContent = user.email;

            // Show/Hide Feedback Button based on permissions
            this.refreshFeedbackButtonVisibility();

            if (!this.accountManager.isVerified()) {
                this.verifyBanner?.classList.remove('hidden');
                this.mainEl?.classList.add('unverified-mode');
            } else {
                this.verifyBanner?.classList.add('hidden');
                this.mainEl?.classList.remove('unverified-mode');
            }

            const adminBtn = document.getElementById('admin-btn');
            const adminView = document.getElementById('admin-view');

            if (this.accountManager.isAdmin()) {
                adminBtn?.classList.remove('hidden');

                // Force switch to admin view on login
                this.mainEl?.classList.add('hidden');
                document.querySelector('.sidebar')?.classList.add('hidden');
                adminView?.classList.remove('hidden');
                this.userDropdown?.classList.add('hidden');

                if (loadAdminSettings) loadAdminSettings();
            } else {
                adminBtn?.classList.add('hidden');
                this.mainEl?.classList.remove('hidden');
                document.querySelector('.sidebar')?.classList.remove('hidden');
                adminView?.classList.add('hidden');
            }

            // Sync UI state from server on login
            this.stateService.syncFromServer(this.accountManager.token).then(() => {


                // Apply Settings
                const savedGains = this.stateService.get('eqGains');
                if (savedGains && updateEQGains) updateEQGains(savedGains);
                if (applyEQ) applyEQ();

                const playbackSpeed = this.stateService.get('playbackSpeed');
                if (this.speedSlider) this.speedSlider.value = playbackSpeed.toString();
                if (this.speedLabel) this.speedLabel.textContent = `${Math.round(playbackSpeed * 100)}%`;

                const pitchInCents = this.stateService.get('pitchInCents') || 0;
                const pitchSlider = document.getElementById('pitch-slider');
                const pitchLabel = document.getElementById('pitch-label');
                if (pitchSlider) pitchSlider.value = pitchInCents.toString();
                if (pitchLabel) pitchLabel.textContent = Math.round(pitchInCents);

                const selectorViewMode = this.stateService.get('selectorViewMode');
                document.querySelectorAll('.view-toggle .btn-small').forEach(btn => {
                    btn.classList.toggle('active', btn.dataset.mode === selectorViewMode);
                });

                if (this.stateService.get('eqCollapsed')) {
                    document.querySelector('.eq-container')?.classList.add('collapsed');
                } else {
                    document.querySelector('.eq-container')?.classList.remove('collapsed');
                }

                const vizState = this.stateService.get('visualizerCollapsed');
                if (vizState) {
                    const vizContainer = document.querySelector('.visualizer-container');
                    vizContainer?.classList.add('collapsed');
                    const toggle = document.getElementById('visualizer-toggle');
                    if (toggle) toggle.textContent = '▼';
                } else {
                    document.querySelector('.visualizer-container')?.classList.remove('collapsed');
                }

                const shortcutsState = this.stateService.get('shortcutsCollapsed');
                if (shortcutsState) {
                    const shortcutsContainer = document.getElementById('shortcuts-container');
                    shortcutsContainer?.classList.add('collapsed');
                    const toggle = document.getElementById('shortcuts-toggle');
                    if (toggle) toggle.textContent = '▼';
                } else {
                    const shortcutsContainer = document.getElementById('shortcuts-container');
                    shortcutsContainer?.classList.remove('collapsed');
                    const toggle = document.getElementById('shortcuts-toggle');
                    if (toggle) toggle.textContent = '▲';
                }

                const sensitivity = this.stateService.get('visualizerThreshold') || 0.0;
                if (this.visualizerSensitivitySlider) this.visualizerSensitivitySlider.value = sensitivity.toString();

                const currentVisMode = this.stateService.get('visualizerMode') || 'hybrid';
                this.updateVisualizerModeUI(currentVisMode);

                const eqCollapsedState = this.stateService.get('eqCollapsed');

                this.refreshFeedbackButtonVisibility();

                if (restoreSession) restoreSession();

                // Initialize dropdown with user files, filtered by cache
                this.refreshFileDropdown();
            });
        } else {
            this.authBtn?.classList.remove('hidden');
            this.userInfo?.classList.add('hidden');
            this.userInfo?.classList.add('hidden');
            document.getElementById('credits-display')?.classList.add('hidden');
            this.setFileDropdownVisibility(false);
            if (this.userDropdown) this.userDropdown.classList.add('hidden');
            this.verifyBanner?.classList.add('hidden');
            document.getElementById('admin-btn')?.classList.add('hidden');
            this.mainEl?.classList.remove('hidden');
            this.mainEl?.classList.remove('unverified-mode');
            document.getElementById('admin-view')?.classList.add('hidden');
            if (resetAppState) resetAppState();
        }
    }

    /**
     * Updates the active state of visualizer mode buttons (Hybrid, Strikes, Sustain).
     * @param {string} mode Active mode string ('hybrid', 'strikes', or 'sustain').
     */
    updateVisualizerModeUI(mode) {
        const buttons = document.querySelectorAll('.visualizer-mode-group button');
        buttons.forEach(btn => {
            if (btn.dataset.mode === mode) {
                btn.classList.add('active');
            } else {
                btn.classList.remove('active');
            }
        });
    }

    async renderProfileModal() {
        const user = this.accountManager.currentUser;
        if (!user) return;

        document.getElementById('profile-modal').classList.remove('hidden');

        // Reset views
        document.getElementById('change-password-form').reset();
        document.getElementById('delete-reason').value = "";
        document.getElementById('delete-feedback').value = "";
        document.getElementById('delete-account-confirm').classList.add('hidden');
        document.getElementById('init-delete-account-btn').classList.remove('hidden');

        const emailDisplay = document.getElementById('profile-email-display');
        if (emailDisplay) emailDisplay.textContent = user.email;

        // Toggle sections based on provider
        if (user.provider === 'email') {
            document.getElementById('profile-password-section').classList.remove('hidden');
            document.getElementById('profile-social-message').classList.add('hidden');
        } else {
            document.getElementById('profile-password-section').classList.add('hidden');
            document.getElementById('profile-social-message').classList.remove('hidden');
            // Update message if provider is not google (though mostly google now)
            const msg = document.querySelector('#profile-social-message strong');
            if (msg) msg.textContent = user.provider.charAt(0).toUpperCase() + user.provider.slice(1);
        }
    }

    async handleChangePasswordSubmit(e) {
        e.preventDefault();
        const currentPassword = document.getElementById('profile-current-password').value;
        const newPassword = document.getElementById('profile-new-password').value;
        const confirmNewPassword = document.getElementById('profile-confirm-new-password').value;

        if (newPassword !== confirmNewPassword) {
            alert('New passwords do not match.');
            return;
        }

        try {
            const btn = e.target.querySelector('button');
            const originalText = btn.textContent;
            btn.textContent = 'Updating...';
            btn.disabled = true;

            await this.accountManager.changePassword(currentPassword, newPassword);

            alert('Password updated successfully!');
            e.target.reset();

            btn.textContent = originalText;
            btn.disabled = false;
        } catch (err) {
            alert(err.message);
            const btn = e.target.querySelector('button');
            btn.textContent = 'Update Password';
            btn.disabled = false;
        }
    }

    async handleDeleteAccountSubmit() {
        const reason = document.getElementById('delete-reason').value;
        const feedback = document.getElementById('delete-feedback').value;

        if (!reason) {
            alert('Please select a reason for leaving.');
            return;
        }

        try {
            const btn = document.getElementById('confirm-delete-account');
            btn.textContent = 'Deleting...';
            btn.disabled = true;

            const fullReason = `${reason}: ${feedback}`;
            await this.accountManager.deleteAccount(fullReason);

            alert('Your account has been deleted. We are sorry to see you go.');
            this.accountManager.logout(); // Clear local state
            document.getElementById('profile-modal').classList.add('hidden');
            window.location.reload(); // Force full reset
        } catch (err) {
            alert(err.message);
            const btn = document.getElementById('confirm-delete-account');
            btn.textContent = 'Permanently Delete';
            btn.disabled = false;
        }
    }

    /**
     * Displays the Upgrade Membership Tiers dialog and loads current pricing.
     */
    async showUpgradeModal() {
        const modal = document.getElementById('upgrade-modal') || this.upgradeModal;
        if (!modal) return;
        modal.classList.remove('hidden');

        const currentUser = this.accountManager?.currentUser;
        const currentTier = (currentUser && currentUser.tier) ? currentUser.tier.toLowerCase() : 'free';
        this.selectedUpgradeTier = currentTier;

        try {
            if (this.accountManager?.getFreeModePricing) {
                this.upgradePricingData = await this.accountManager.getFreeModePricing();
            }
        } catch (err) {
            console.warn('[UIManager] Failed to load subscription pricing:', err);
        }

        if (!this.upgradePricingData) {
            this.upgradePricingData = {
                advanced: { monthly: 12, yearly: 100 },
                pro: { monthly: null, yearly: 199 },
                unlimited: { monthly: null, yearly: 1999 }
            };
        }

        this.renderUpgradeTiers();
        this.updateUpgradeActionButton();
    }

    /**
     * Hides the Upgrade Membership Tiers dialog.
     */
    hideUpgradeModal() {
        const modal = document.getElementById('upgrade-modal') || this.upgradeModal;
        if (modal) modal.classList.add('hidden');
    }

    /**
     * Formats price and period display for a tier given the active billing toggle.
     * @param {Object} tierData - Pricing information with monthly and yearly properties.
     * @returns {{ price: string, period: string }} Formatted price and period.
     */
    getUpgradeTierPriceDisplay(tierData) {
        return formatTierPricing(tierData, this.isUpgradeYearlyBilling);
    }

    /**
     * Renders available membership tiers inside the upgrade modal.
     */
    renderUpgradeTiers() {
        const container = document.getElementById('upgrade-tiers-container');
        if (!container) return;

        const pricing = this.upgradePricingData || {
            advanced: { monthly: 12, yearly: 100 },
            pro: { monthly: null, yearly: 199 },
            unlimited: { monthly: null, yearly: 1999 }
        };

        const currentUser = this.accountManager?.currentUser;
        const currentTier = (currentUser && currentUser.tier) ? currentUser.tier.toLowerCase() : 'free';
        const selectedTier = this.selectedUpgradeTier || currentTier;

        const tiers = SUBSCRIPTION_TIERS.map(tier => {
            const pricingInfo = pricing[tier.key];
            const display = (tier.price === '$0') ? { price: '$0', period: 'Free forever' } : this.getUpgradeTierPriceDisplay(pricingInfo);
            return {
                ...tier,
                price: display.price,
                period: display.period
            };
        });

        container.innerHTML = tiers.map(tier => {
            const isCurrent = currentTier === tier.key;
            const isSelected = selectedTier === tier.key;

            let badgeHtml = '';
            if (isSelected) {
                badgeHtml = '<div class="upgrade-tier-badge badge-selected">Selected</div>';
            } else if (isCurrent) {
                badgeHtml = '<div class="upgrade-tier-badge badge-current">Current Plan</div>';
            } else if (tier.featured) {
                badgeHtml = '<div class="upgrade-tier-badge">Most Popular</div>';
            }

            let btnClass = 'btn btn-secondary upgrade-tier-btn';
            let btnText = `Select ${tier.name}`;
            if (isSelected) {
                btnClass = 'btn upgrade-tier-btn btn-selected';
                btnText = '✓ Selected';
            } else if (isCurrent) {
                btnClass = 'btn upgrade-tier-btn btn-current';
                btnText = 'Current Plan';
            }

            const featuresHtml = tier.features.map(f => `
                <li class="${f.enabled ? '' : 'disabled'}">
                    <span class="check">${f.enabled ? '✓' : '✕'}</span>
                    <span>${f.text}</span>
                </li>
            `).join('');

            return `
                <div class="upgrade-tier-card glass ${tier.featured ? 'featured' : ''} ${isSelected ? 'selected' : ''} ${isCurrent ? 'current-plan' : ''}" data-tier="${tier.key}">
                    ${badgeHtml}
                    <div class="upgrade-tier-header">
                        <div class="upgrade-tier-name">${tier.name}</div>
                        <div class="upgrade-tier-desc">${tier.desc}</div>
                    </div>
                    <div class="upgrade-tier-price-box">
                        <div class="upgrade-tier-price">${tier.price}</div>
                        <div class="upgrade-tier-period">${tier.period}</div>
                    </div>
                    <ul class="upgrade-tier-features">
                        ${featuresHtml}
                    </ul>
                    <button type="button" class="${btnClass}" data-tier="${tier.key}">
                        ${btnText}
                    </button>
                </div>
            `;
        }).join('');

        // Attach tier selection event handlers
        container.querySelectorAll('.upgrade-tier-card, .upgrade-tier-btn').forEach(el => {
            el.addEventListener('click', (e) => {
                e.stopPropagation();
                const tierKey = el.dataset.tier;
                if (tierKey) {
                    this.selectUpgradeTier(tierKey);
                }
            });
        });
    }

    /**
     * Updates the footer action button in the upgrade modal (Close vs Purchase).
     */
    updateUpgradeActionButton() {
        const btn = document.getElementById('close-upgrade-btn');
        if (!btn) return;

        const currentUser = this.accountManager?.currentUser;
        const currentTier = (currentUser && currentUser.tier) ? currentUser.tier.toLowerCase() : 'free';
        const selectedTier = this.selectedUpgradeTier || currentTier;

        // If the user selected a different paid tier from their current plan, switch button to "Purchase"
        const isPurchaseMode = selectedTier !== currentTier && selectedTier !== 'free';

        if (isPurchaseMode) {
            btn.textContent = 'Purchase';
            btn.className = 'btn btn-primary';
            btn.dataset.action = 'purchase';
        } else {
            btn.textContent = 'Close';
            btn.className = 'btn btn-secondary';
            btn.dataset.action = 'close';
        }
        btn.disabled = false;
    }

    /**
     * Updates the selected tier in the upgrade dialog.
     * @param {string} tierKey - The key of the selected tier ('free', 'advanced', 'pro', 'unlimited').
     */
    selectUpgradeTier(tierKey) {
        this.selectedUpgradeTier = tierKey;
        this.renderUpgradeTiers();
        this.updateUpgradeActionButton();
    }

    /**
     * Initiates checkout for the selected subscription tier.
     * @param {HTMLElement} [btn=null] - The purchase button element.
     */
    async handleUpgradePurchase(btn = null) {
        const currentUser = this.accountManager?.currentUser;
        if (!currentUser || !this.accountManager?.token) {
            // Not authenticated -> prompt sign up / sign in
            this.hideUpgradeModal();
            this.loginModal?.classList.remove('hidden');
            return;
        }

        const selectedTierKey = this.selectedUpgradeTier;
        const tierConfig = SUBSCRIPTION_TIERS.find(t => t.key === selectedTierKey);
        if (!tierConfig || !tierConfig.variantIds) {
            alert('Selected plan is not available for online purchase.');
            return;
        }

        const billingKey = this.isUpgradeYearlyBilling ? 'yearly' : 'monthly';
        const variantId = tierConfig.variantIds[billingKey] || tierConfig.variantIds.yearly || tierConfig.variantIds.monthly;

        if (!variantId) {
            alert('Checkout variant is not configured for this plan frequency.');
            return;
        }

        try {
            if (btn) {
                btn.textContent = 'Preparing Checkout...';
                btn.disabled = true;
            }

            const { url } = await this.accountManager.createCheckoutSession({ variantId });
            window.location.href = url;
        } catch (err) {
            console.error('[Subscription Checkout] Failed:', err);
            alert('Failed to start checkout: ' + err.message);
            if (btn) {
                btn.textContent = 'Purchase';
                btn.disabled = false;
            }
        }
    }

    async showPurchaseModal() {
        if (featureFlags.isEnabled('free_mode')) {
            return;
        }
        const modal = document.getElementById('purchase-modal');
        modal.classList.remove('hidden');

        const container = document.getElementById('purchase-options');
        try {
            const tiers = await this.accountManager.getPricing();
            if (tiers.length === 0) {
                container.innerHTML = '<p>No purchasing options available at this time.</p>';
                return;
            }

            container.innerHTML = tiers.map(tier => `
                <div class="pricing-card glass" style="padding: 1.5rem; display: flex; flex-direction: column; align-items: center; text-align: center; border: 1px solid var(--glass-border);">
                    <h3 style="margin-bottom: 0.5rem;">${tier.name}</h3>
                    <div style="font-size: 2rem; font-weight: bold; margin-bottom: 1rem;">$${tier.price.toFixed(2)}</div>
                    <div style="margin-bottom: 1.5rem; color: var(--text-secondary);">${tier.credits} Licenses</div>
                    <button class="btn btn-primary purchase-btn" data-id="${tier.id}" style="width: 100%;">Purchase</button>
                </div>
            `).join('');

            container.querySelectorAll('.purchase-btn').forEach(btn => {
                btn.addEventListener('click', async () => {
                    const tierId = parseInt(btn.dataset.id);
                    await this.handlePurchase(tierId, btn);
                });
            });

        } catch (err) {
            container.innerHTML = `<p style="color: var(--error-color);">Failed to load pricing: ${err.message}</p>`;
        }
    }

    async handlePurchase(tierId, btn) {
        try {
            btn.textContent = 'Preparing Checkout...';
            btn.disabled = true;

            const { url } = await this.accountManager.createCheckoutSession(tierId);
            
            // Redirect to Lemon Squeezy checkout
            window.location.href = url;
        } catch (err) {
            console.error('[Purchase] Checkout failed:', err);
            alert('Failed to start checkout: ' + err.message);
            btn.textContent = 'Purchase';
            btn.disabled = false;
        }
    }

    showCreditWarningModal() {
        return new Promise((resolve) => {
            const isFreeMode = featureFlags.isEnabled('free_mode');
            const isBeta = this.accountManager.currentUser?.beta_mode;
            const modalBody = this.warningModal.querySelector('.modal-body');

            // Save original content if not saved
            if (!this._originalWarningContent) {
                this._originalWarningContent = modalBody.innerHTML;
            }

            if (isFreeMode) {
                modalBody.innerHTML = `
                    <p>You already have a file with this name, but the file content is different.</p>
                    <p style="margin-top: 1rem; color: var(--text-secondary);">Do you want to proceed?</p>
                `;
            } else if (isBeta) {
                modalBody.innerHTML = `
                    <p>You already have a licensed file with this name, but the file content is different.</p>
                    <p><strong>Beta Mode Active:</strong> This action will NOT consume any credits.</p>
                    <p style="margin-top: 1rem; color: var(--text-secondary);">Do you want to proceed?</p>
                `;
            } else {
                modalBody.innerHTML = this._originalWarningContent;
            }

            this.warningModal.classList.remove('hidden');

            const cleanup = () => {
                this.warningModal.classList.add('hidden');
                this.warningCancelBtn.replaceWith(this.warningCancelBtn.cloneNode(true));
                this.warningProceedBtn.replaceWith(this.warningProceedBtn.cloneNode(true));
                // Re-fetch to update references (optional but safer if called repeatedly)
                this.warningCancelBtn = document.getElementById('warning-cancel-btn');
                this.warningProceedBtn = document.getElementById('warning-proceed-btn');
            };

            this.warningCancelBtn.addEventListener('click', () => {
                cleanup();
                resolve(false);
            }, { once: true });

            this.warningProceedBtn.addEventListener('click', () => {
                cleanup();
                resolve(true);
            }, { once: true });
        });
    }

    async handleClearCache() {
        if (confirm('Are you sure you want to clear the file cache? This will remove all processed files and you will need to reload them.')) {
            try {
                const btn = this.clearCacheBtn;
                const originalText = btn.textContent;
                btn.textContent = 'Clearing...';
                btn.disabled = true;

                await fileStorage.clear();

                // Clear the persistence state so we don't try to restore a missing file
                // Also clear fileStates since the files are gone
                await this.stateService.saveState({ lastFile: null, fileStates: {} }, this.accountManager.token);

                alert('Cache cleared successfully.');

                // Reset UI to initial state
                const statusMsg = document.getElementById('status-message');
                if (statusMsg) statusMsg.innerHTML = 'Ready to load audio...'; // Use innerHTML to clear any buttons

                const fileInfo = document.getElementById('file-info');
                if (fileInfo) fileInfo.textContent = '';

                // STRICTLY clear and hide dropdown
                this.fileDropdownOptions = document.getElementById('file-dropdown-options'); // Re-acquire to be safe
                if (this.fileDropdownOptions) {
                    this.fileDropdownOptions.innerHTML = '';
                    this.fileDropdownOptions.classList.add('hidden');
                }

                if (this.fileDropdownContainer) {
                    this.fileDropdownContainer.classList.add('hidden');
                }

                this.setSelectedFile(null);

                // Reset button state
                btn.textContent = originalText;
                btn.disabled = false;

                // Reset entire app state (visualizations, buffers, etc.)
                if (this.resetAppStateCallback) {
                    this.resetAppStateCallback();
                } else {
                    // Fallback UI reset
                    const statusMsg = document.getElementById('status-message');
                    if (statusMsg) statusMsg.innerHTML = 'Ready to load audio...';
                    const fileInfo = document.getElementById('file-info');
                    if (fileInfo) fileInfo.textContent = '';
                }
            } catch (err) {
                alert('Failed to clear cache: ' + err.message);
                this.clearCacheBtn.textContent = 'Clear Cache';
                this.clearCacheBtn.disabled = false;
            }
        }
    }

    async handleFeedbackSubmit(e) {
        e.preventDefault();
        const subject = document.getElementById('feedback-subject').value;
        const body = this.feedbackBody.value;
        const submitBtn = this.feedbackForm.querySelector('button[type="submit"]');
        const originalText = submitBtn.textContent;

        try {
            submitBtn.textContent = 'Sending...';
            submitBtn.disabled = true;
            await this.accountManager.submitFeedback(subject, body);
            alert('Feedback submitted successfully. Thank you!');
            this.closeFeedbackModal();
        } catch (err) {
            alert('Failed to submit feedback: ' + err.message);
        } finally {
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
        }
    }

    initFileDropdown(user, loadFileCallback, cachedHashes = []) {
        if (!this.fileDropdownContainer) return;

        // Reset
        this.fileDropdownOptions.innerHTML = '';

        // If no cached hashes provided (e.g. first load), we might want to default to nothing?
        // Or should we trust the caller? The requirement is "only cached files".
        // If cachedHashes is empty, the dropdown is empty.

        const validFiles = user.licensedTranscriptions.filter(f => cachedHashes.includes(f.file_hash));

        if (validFiles.length > 0) {
            // Populate Options
            validFiles.forEach(file => {
                this.addFileToDropdownInternal(file.filename, file.file_hash, loadFileCallback);
            });

            this.setFileDropdownVisibility(true); // Show if we have files
        } else {
            this.setFileDropdownVisibility(false);
        }

        // Always re-bind trigger to handle toggle logic, even if initially empty (it might get populated later)
        // Bind trigger click
        // Clone to remove old listeners
        const newTrigger = this.fileDropdownTrigger.cloneNode(true);
        this.fileDropdownTrigger.parentNode.replaceChild(newTrigger, this.fileDropdownTrigger);
        this.fileDropdownTrigger = newTrigger;

        // Re-acquire label reference after clone
        this.currentFileLabel = this.fileDropdownTrigger.querySelector('#current-file-label');

        this.fileDropdownTrigger.addEventListener('click', (e) => {
            e.stopPropagation();
            // toggle only if we have children? Or always? 
            // Better to only toggle if visible/has children, but the visibility logic handles the container.
            this.fileDropdownOptions.classList.toggle('hidden');
        });
    }

    addFileToDropdown(filename, hash, loadFileCallback) {
        // Check if already exists
        const existing = Array.from(this.fileDropdownOptions.children).find(child => child.dataset.hash === hash);
        if (existing) return;

        this.addFileToDropdownInternal(filename, hash, loadFileCallback);
        this.setFileDropdownVisibility(true);
    }

    addFileToDropdownInternal(filename, hash, loadFileCallback) {
        const opt = document.createElement('div');
        opt.className = 'dropdown-option';
        opt.textContent = filename;
        opt.dataset.hash = hash;

        opt.addEventListener('click', (e) => {
            const h = e.target.dataset.hash;
            const n = e.target.textContent;

            if (loadFileCallback) loadFileCallback(h, n);
            this.setSelectedFile(n);
            this.fileDropdownOptions.classList.add('hidden');
        });

        this.fileDropdownOptions.appendChild(opt);
    }

    setFileDropdownVisibility(isVisible) {
        if (this.fileDropdownContainer) {
            // Only show if we have actual files
            if (isVisible && this.fileDropdownOptions.children.length > 0) {
                this.fileDropdownContainer.classList.remove('hidden');
            } else {
                this.fileDropdownContainer.classList.add('hidden');
            }
        }
    }

    closeFeedbackModal() {
        this.feedbackModal.classList.add('hidden');
        this.feedbackForm.reset();
        if (this.charCount) this.charCount.textContent = '0 / 512';
    }

    setSelectedFile(filename) {
        if (this.currentFileLabel) {
            this.currentFileLabel.textContent = filename || 'Switch File...';
        }
    }

    async refreshFileDropdown() {
        try {
            const cachedHashes = await fileStorage.getCachedHashes();
            const user = this.accountManager.currentUser;
            // Use stored callback if available, otherwise we might fail to load files if refreshed from outside 
            // but usually updateAuthUI sets it.
            this.initFileDropdown(user, this.loadFileCallback, cachedHashes);
        } catch (err) {
            console.error("Failed to refresh file dropdown", err);
        }
    }

    showPurchaseSuccess() {
        const isFreeMode = featureFlags.isEnabled('free_mode');
        const userTier = this.accountManager?.currentUser?.tier || 'membership';
        const tierName = userTier.charAt(0).toUpperCase() + userTier.slice(1);

        const statusMsgText = isFreeMode 
            ? `🎉 Subscription active! You are on the ${tierName} plan.`
            : '🎉 Purchase successful! Credits updated.';
        
        const alertMsgText = isFreeMode
            ? `🎉 Thank you! Your account has been upgraded to the ${tierName} tier.`
            : '🎉 Thank you! Your credits have been updated successfully.';

        // Show a temporary success message
        const statusMsg = document.getElementById('status-message');
        if (statusMsg) {
            const original = statusMsg.textContent;
            statusMsg.textContent = statusMsgText;
            statusMsg.style.color = '#4CAF50';
            setTimeout(() => {
                statusMsg.textContent = original;
                statusMsg.style.color = '';
            }, 5000);
        }
        
        alert(alertMsgText);
    }
}
